<template>
  <div :class="cssSizeClass">
    <span v-dompurify-html="content" :class="cssContentClass"></span>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

export default defineComponent({
  name: 'Badge',
  props: {
    count: {
      type: [String, Number],
      required: true
    },
    isIcon: {
      type: Boolean,
      default: false
    },
    color: {
      type: String,
      default: 'blue'
    },
    size: {
      type: String,
      default: 'medium'
    },
    longCount: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    cssContentClass(): string {
      const classes = ['badge-wrapper__content', `badge-wrapper__content--${this.color}`]

      if (!this.isIcon && this.longCount) {
        classes.push('badge-wrapper__content--long-content')
      }

      return classes.join(' ')
    },
    cssSizeClass(): string {
      const classes = ['badge-wrapper', `badge-wrapper--${this.size || 'medium'}`]
      return classes.join(' ')
    },
    content(): string | number {
      return !this.isIcon ? this.formatCount(this.count) : this.setIcon(this.count)
    }
  },
  methods: {
    formatCount(content: string | number): string | number {
      return isNaN(Number(content)) ? String(content).trim() : parseInt(String(content), 10)
    },
    setIcon(content: string | number): string {
      return `<i class="far fa-${content}"/>`
    }
  }
})
</script>

<style lang="scss">
/*-----BADGE COMPONENT-------*/
@mixin roundedBadge($size: 3, $ratio: 1.2) {
  display: flex;
  place-items: center;
  place-content: center;

  & > .badge-wrapper__content {
    --size: #{$size}rem;
    --ratio: #{$ratio};
    font-weight: 700;
    letter-spacing: unset;
    display: list-item;
    width: calc(var(--size) * var(--ratio));
    height: calc(var(--size) * var(--ratio));
    border-radius: 50%;
    font-size: calc(var(--size) * 0.55);
    line-height: calc(var(--size) * var(--ratio));
    text-align: center;
    background: var(--badge-bg-color, var(--dealer-primary-color));
    color: #fefefe;

    &--long-content {
      position: inherit;
      font-weight: 500;
      padding: 0em 0.5em;
      border-radius: 0.5em;
    }
  }
}

.badge-wrapper {
  &--very-small {
    @include roundedBadge(1.44);

    .badge-wrapper__content {
      &--long-content {
        width: unset !important;
        height: unset !important;
      }
    }
  }

  &--small {
    @include roundedBadge(2.1);
  }

  &--medium {
    @include roundedBadge(2.4);
  }

  &--large {
    @include roundedBadge(2.8);
  }
}
</style>
